<template>
  <div>
    <v-list-item
      :class="[
        'notification-item',
        notification.read ? 'notification-read' : 'notification-unread',
      ]"
      :ripple="true"
      density="comfortable"
      @click="navigateToContent"
    >
      <!-- Avatar/Icon Section -->
      <template #prepend>
        <v-avatar
          :color="notification.read ? 'grey-darken-1' : 'primary'"
          class="mr-3"
          size="36"
        >
          <span class="text-body-1 font-weight-medium white--text">
            {{ notification.user_short }}
          </span>
        </v-avatar>
      </template>

      <!-- Content Section -->
      <v-list-item-title
        :class="[
          'font-weight-medium mb-1',
          notification.read ? 'text-grey-darken-1' : 'text-primary',
        ]"
      >
        <span v-html="$sanitizeHtml(notification.text)" />
      </v-list-item-title>

      <v-list-item-subtitle class="text-caption">
        {{ formatRelativeTime(notification.created_at) }}
      </v-list-item-subtitle>

      <!-- Status indicator -->
      <template #append>
        <v-btn
          :icon="
            notification.read ? 'mdi-email-open-outline' : 'mdi-email-outline'
          "
          :color="notification.read ? 'grey' : 'primary'"
          size="small"
          variant="text"
          :disabled="notification.read"
          @click.stop="markAsRead"
        />
      </template>
    </v-list-item>
    <v-divider v-if="!last" />
  </div>
</template>

<script>
import moment from "moment";
import { useNotificationsStoreRefactor } from "../../stores/notificationsStoreRefactor";
import { useRouter } from "vue-router";

export default {
  name: "NotificationItem",
  props: ["notification", "last"],
  setup(props) {
    const notificationsStore = useNotificationsStoreRefactor();
    const router = useRouter();

    const navigateToContent = async () => {
      // Mark as read if not already read
      if (!props.notification.read) {
        try {
          await notificationsStore.read(props.notification.id);
        } catch (error) {
          console.error("Error marking notification as read:", error);
        }
      }

      // Check if link exists first
      if (!props.notification.link) {
        console.error(
          "NotificationItem: No link available for navigation",
          props.notification,
        );
        return;
      }

      // Extract various IDs from the link
      const dayReportMatch =
        props.notification.link.match(/\/day_reports\/(\d+)/);
      const reportMatch = props.notification.link.match(/\/reports\/(\d+)/);
      const accountReportMatch = props.notification.link.match(
        /\/accounts\/(\d+)\/reports\/(\d+)/,
      );
      const deadlineMatch = props.notification.notification_type == "deadline";

      if (dayReportMatch) {
        const dayReportId = dayReportMatch[1];
        const commentId = props.notification.object_id;

        console.log(
          `NotificationItem: Navigating to day report ${dayReportId} with comment ${commentId}`,
        );

        // Navigate to the day report detail page with tab parameter in query
        const navigationTarget = {
          path: `/day_reports/${dayReportId}`,
          query: {
            tab: "comments",
            comment: commentId, // Use query parameter instead of hash
          },
        };

        console.log("NotificationItem: Navigation target:", navigationTarget);

        // Navigate
        try {
          await router.push(navigationTarget);
        } catch (error) {
          console.error("NotificationItem: Navigation error:", error);
        }
      } else if (reportMatch || accountReportMatch) {
        // This is a report notification - could be from /reports/:id or /accounts/:accountId/reports/:id
        let reportId,
          accountId = null;

        if (accountReportMatch) {
          accountId = accountReportMatch[1];
          reportId = accountReportMatch[2];
          console.log(
            `NotificationItem: Navigating to account ${accountId} report ${reportId}`,
          );
        } else {
          reportId = reportMatch[1];
          console.log(`NotificationItem: Navigating to report ${reportId}`);
        }

        const commentId = props.notification.object_id;

        // Build navigation target - prefer account context route if available
        const navigationTarget = {
          path: accountId
            ? `/accounts/${accountId}/reports/${reportId}`
            : `/reports/${reportId}`,
          query: commentId ? { comment: commentId } : {},
        };

        console.log(
          "NotificationItem: Report navigation target:",
          navigationTarget,
        );

        // Navigate
        try {
          await router.push(navigationTarget);
        } catch (error) {
          console.error("NotificationItem: Navigation error:", error);
        }
      } else if (deadlineMatch) {
        // This is a deadline notification
        const deadlineId = props.notification.object_id;
        console.log(
          `NotificationItem: Navigating to deadlines with deadline ID ${deadlineId}`,
        );

        // Navigate to the deadlines page with the deadline ID
        const navigationTarget = {
          path: "/",
          query: {
            tab: "taken",
            deadline: deadlineId,
          },
        };

        console.log("NotificationItem: Navigation target:", navigationTarget);

        // Navigate
        try {
          await router.push(navigationTarget);

          // Force a refresh after navigation to ensure proper highlighting
          setTimeout(() => {
            const element = document.getElementById(`d${deadlineId}`);
            if (element) {
              console.log(
                `NotificationItem: Found deadline element after navigation, scrolling to it`,
              );
              element.scrollIntoView({ behavior: "smooth", block: "center" });

              // Add highlight animation
              element.classList.add("highlight-deadline");

              // Remove highlight after animation
              setTimeout(() => {
                element.classList.remove("highlight-deadline");
              }, 2000);
            } else {
              console.log(
                `NotificationItem: Could not find deadline element d${deadlineId} after navigation`,
              );
            }
          }, 800); // Give more time for navigation to complete and components to render
        } catch (error) {
          console.error("NotificationItem: Navigation error:", error);
        }
      } else {
        // Fallback to original link if not a recognized pattern
        console.log(
          "NotificationItem: Not a recognized link pattern, using original:",
          props.notification.link,
        );
        router.push(props.notification.link);
      }
    };

    const markAsRead = async () => {
      if (!props.notification.read) {
        try {
          await notificationsStore.read(props.notification.id);
        } catch (error) {
          console.error("Error marking notification as read:", error);
        }
      }
    };

    const formatRelativeTime = (dateTime) => {
      return moment(dateTime).from(moment());
    };

    return {
      navigateToContent,
      markAsRead,
      formatRelativeTime,
    };
  },
};
</script>

<style scoped>
.notification-item {
  transition: all 0.2s ease;
}

.notification-unread {
  background-color: rgb(var(--v-theme-primary-lighten-5));
  border-left: 4px solid rgb(var(--v-theme-primary));
}

.notification-read {
  background-color: rgb(var(--v-theme-surface));
  border-left: 4px solid transparent;
  opacity: 0.8;
}

.notification-item:hover {
  background-color: rgb(var(--v-theme-primary-lighten-4)) !important;
  opacity: 1;
}

@keyframes highlight-pulse {
  0% {
    background-color: rgba(var(--v-theme-primary), 0.1);
    transform: scale(1);
  }
  50% {
    background-color: rgba(var(--v-theme-primary), 0.2);
    transform: scale(1.02);
  }
  100% {
    background-color: rgba(var(--v-theme-primary), 0.1);
    transform: scale(1);
  }
}

:deep(.highlight-comment) {
  animation: highlight-pulse 2s ease;
  border-radius: 8px;
  box-shadow: 0 0 0 4px rgba(var(--v-theme-primary), 0.1);
}
</style>
